import Link from 'next/link'
import React from 'react'

import { Icon } from '@/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons/faArrowRight'
import { faGift } from '@fortawesome/pro-solid-svg-icons/faGift'
import { Container, styled } from '@mui/material'

/**
 * First-time-buyer banner. Promotes the BOGO 50% hardcover deal. The backend
 * cart pricing (`/api/storefront/cart/quote/` + checkout) applies it
 * automatically for users with no prior physical-book orders once the cart
 * holds 2+ hardcovers — no code needs to be entered.
 *
 * Shown to everyone; the discount itself is gated server-side, so we don't
 * have to do anything clever on the frontend to hide it.
 */
export const LimitedTimeBanner: React.FC = () => (
  <StyledBanner href="/shop" aria-label="First-time offer: buy one hardcover, get one 50% off">
    <StyledInner maxWidth="lg">
      <StyledContent>
        <StyledBadge>
          <Icon icon={faGift} sx={{ fontSize: pxToRem(12) }} />
          <span>First-time offer</span>
        </StyledBadge>
        <StyledMessage>
          <StyledStrong>Buy one hardcover, get the second 50% off</StyledStrong>
          <StyledSubtle>
            {' — applied automatically in your cart'}
          </StyledSubtle>
        </StyledMessage>
        <StyledCta>
          <span>Make your free story</span>
          <Icon icon={faArrowRight} sx={{ fontSize: pxToRem(12) }} />
        </StyledCta>
      </StyledContent>
    </StyledInner>
  </StyledBanner>
)

const StyledBanner = styled(Link)(({ theme }) => ({
  display: 'block',
  width: '100%',
  backgroundColor: '#8B2E1D',
  color: '#FFFFFF',
  textDecoration: 'none',
  position: 'relative',
  cursor: 'pointer',
  transition: 'filter 0.2s ease',
  zIndex: 2,

  '&:hover': {
    filter: 'brightness(1.08)',
  },
}))

const StyledInner = styled(Container)(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(1.5),

  [theme.breakpoints.up('md')]: {
    paddingTop: theme.spacing(1.25),
    paddingBottom: theme.spacing(1.25),
    gap: theme.spacing(2),
  },
}))

const StyledContent = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  flexWrap: 'wrap',
  justifyContent: 'center',
  fontSize: pxToRem(13),
  lineHeight: 1.3,

  [theme.breakpoints.up('md')]: {
    gap: theme.spacing(2),
    fontSize: pxToRem(14),
    flexWrap: 'nowrap',
  },
}))

const StyledBadge = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  backgroundColor: 'rgba(255,255,255,0.22)',
  padding: theme.spacing(0.35, 1),
  borderRadius: pxToRem(999),
  fontSize: pxToRem(11),
  fontWeight: 700,
  letterSpacing: pxToRem(0.8),
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
}))

const StyledMessage = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  gap: theme.spacing(0.5),
  justifyContent: 'center',
}))

const StyledStrong = styled('span')({
  fontWeight: 700,
})

const StyledSubtle = styled('span')({
  opacity: 0.92,
})


const StyledCta = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  fontWeight: 700,
  textDecoration: 'underline',
  textUnderlineOffset: pxToRem(3),

  [theme.breakpoints.up('md')]: {
    marginLeft: theme.spacing(0.5),
  },
}))
